import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Download, Printer, Copy, CheckCircle2, MapPin, HardHat, Clock } from "lucide-react";
import { toast } from "sonner";

export default function QRPosterModal({ open, onClose, trade, project }) {
  const [copied, setCopied] = useState(false);

  if (!trade) return null;

  const link = trade.siteReadyLink || `https://app.easypermit.com.au/pass/${trade.id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="qr-poster-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-slate-700" />
            QR Site Access Poster
          </DialogTitle>
        </DialogHeader>

        {/* Poster Preview */}
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 bg-white">
          <div className="text-center space-y-4">
            {/* Header */}
            <div className="bg-slate-900 text-white rounded-lg px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">easypermit</p>
              <p className="text-sm font-bold mt-0.5">SiteReady Trade Pass</p>
            </div>

            {/* QR Code Placeholder */}
            <div className="mx-auto w-40 h-40 bg-slate-100 border-2 border-slate-200 rounded-xl flex items-center justify-center relative">
              <div className="grid grid-cols-5 gap-1 p-3">
                {Array.from({ length: 25 }).map((_, i) => (
                  <div key={i} className={`w-4 h-4 rounded-sm ${Math.random() > 0.35 ? 'bg-slate-800' : 'bg-white'}`} />
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-white rounded border border-slate-200 flex items-center justify-center">
                  <HardHat className="w-4 h-4 text-slate-700" />
                </div>
              </div>
            </div>

            <p className="text-[10px] text-slate-400 font-mono break-all px-4">{link}</p>

            {/* Trade Info */}
            <div className="bg-slate-50 rounded-lg p-3 text-left space-y-2">
              <div className="flex items-center gap-2">
                <HardHat className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-bold text-slate-800">{trade.trade}</span>
                <Badge className="text-[8px] bg-blue-50 text-blue-700 border-blue-200 ml-auto">{trade.company}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs text-slate-600">{project?.address || trade.project}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs text-slate-600">Stage: {trade.stage}</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-[10px] text-slate-500 font-medium">Scan to view your Trade Pack, check in, and upload work proof</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-2">
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleCopy} data-testid="qr-copy-link">
            {copied ? <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
            {copied ? "Copied" : "Copy Link"}
          </Button>
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => toast.success("Poster downloaded")} data-testid="qr-download">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download
          </Button>
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => toast.success("Sent to printer")} data-testid="qr-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
